import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { PAGE_INDEX, TABLE_OPTIONS } from '~/features/manage-parties/constants';
import { AddPartyModal } from '~/features/manage-parties/modals/AddPartyModal';
import { DeletePartyModal } from '~/features/manage-parties/modals/DeletePartyModal';
import { UpdatePartyModal } from '~/features/manage-parties/modals/UpdatePartyModal';
import { UploadPartyModal } from '~/features/manage-parties/modals/UploadPartyModal';
import {
  BasePartyModalProps,
  ExtendedPartyModalProps,
  PaginationParams,
  Party,
  PartyModalProps,
} from '~/features/manage-parties/types';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

interface DeletedParty {
  status: 'deleted' | 'failed';
  party: Party;
}

const Page = () => {
  const intl = useIntl();
  const { params } = useRouting();

  const clientId = Number(params.clientId);

  const { toast } = useToast();

  const partyLoadingErrorMessage = intl.formatMessage({
    id: 'ManagePartyLists.loadPartyListsError',
    defaultMessage: 'Failed to load party lists, try again.',
    description: 'Error message when loading party lists fails',
  });

  const noPartyFoundMessage = intl.formatMessage({
    id: 'ManagePartyLists.noPartyListsFound',
    defaultMessage: 'No Party Lists found.',
    description: 'Message when no party lists are found',
  });

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'ManagePartyLists.navTitle',
        defaultMessage: 'Manage Party Lists',
        description: 'Navigation title for manage party lists page',
      }),
      pathname: RoutePathType.AdminConsoleClientManagePartyLists,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId,
    },
    intl,
  });

  const [modalProps, setModalProps] = useState<Nullable<PartyModalProps>>(null);
  const [internalPage, setInternalPage] = useState(PAGE_INDEX);
  const [externalPage, setExternalPage] = useState(PAGE_INDEX);

  const {
    data: externalParties = { results: [], count: 0 },
    isFetching: isFetchingExternalParties,
    error: externalPartiesError,
  } = api.endpoints.getParties.useQuery(
    {
      clientId,
      isInternal: false,
      page: externalPage,
    },
    { skip: !clientId },
  );

  const {
    data: internalParties = { results: [], count: 0 },
    isFetching: isFetchingInternalParties,
    error: internalPartiesError,
  } = api.endpoints.getParties.useQuery(
    {
      clientId,
      isInternal: true,
      page: internalPage,
    },
    { skip: !clientId },
  );

  const [deleteParties] = api.endpoints.deleteParties.useMutation();

  const handleHideModal = () => {
    setModalProps(null);
  };

  const columns = [
    {
      key: 'name',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'ManagePartyLists.partyNameColumn',
        defaultMessage: 'Party Name',
        description: 'Party name column header',
      }),
      mapCellProps: (d: Party) => ({
        text: d.name,
      }),
      width: 'auto',
    },
  ];

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'ManagePartyLists.editPartyAction',
        defaultMessage: 'Edit Party',
        description: 'Edit party row action label',
      }),
      onClick: (party: Party) => {
        setModalProps({
          action: 'edit',
          clientId,
          party,
          onHide: handleHideModal,
        });
      },
    },
    {
      label: intl.formatMessage({
        id: 'ManagePartyLists.deletePartyAction',
        defaultMessage: 'Delete Party',
        description: 'Delete party row action label',
      }),
      onClick: (party: Party) => {
        setModalProps({
          action: 'delete',
          clientId,
          party,
          onHide: handleHideModal,
        });
      },
    },
  ];

  const getBulkActions = () => {
    return [
      {
        label: intl.formatMessage({
          id: 'ManagePartyLists.deleteBulkAction',
          defaultMessage: 'Delete',
          description: 'Delete bulk action label',
        }),
        onClick: (selectedParties: any) => {
          const { data: parties, name } = selectedParties;
          const isInternal = name === 'Internal Party List';
          deleteParties({ parties, clientId, isInternal })
            .unwrap()
            .then((deletedParties: DeletedParty[]) => {
              const deletedPartyNames = deletedParties
                .filter((party) => party.status === 'deleted')
                .map((item) => item.party.name)
                .join(', ');

              const failedPartyNames = deletedParties
                .filter((party) => party.status === 'failed')
                .map((item) => item.party.name)
                .join(', ');
              if (deletedPartyNames) {
                toast({
                  message: intl.formatMessage(
                    {
                      id: 'ManagePartyLists.partiesDeletedSuccess',
                      defaultMessage:
                        'Parties "{partyNames}" have been successfully removed.',
                      description:
                        'Success message when parties are deleted. Example: partyNames = "Party A, Party B"',
                    },
                    {
                      partyNames: deletedPartyNames,
                    },
                  ),
                  status: 'success',
                });
              }
              if (failedPartyNames) {
                toast({
                  message: intl.formatMessage(
                    {
                      id: 'ManagePartyLists.partiesDeletedError',
                      defaultMessage:
                        'Failed to remove parties "{partyNames}".',
                      description:
                        'Error message when party deletion fails. Example: partyNames = "Party A, Party B"',
                    },
                    {
                      partyNames: failedPartyNames,
                    },
                  ),
                  status: 'danger',
                });
              }
            })
            .catch((error) => {
              toast({
                message: `${error.response.data.detail}`,
                status: 'danger',
              });
            });
        },
      },
    ];
  };

  const children = (
    <Layout preset="sections">
      {[true, false].map((isInternal) => {
        const partyConfigs = isInternal
          ? {
              data: internalParties,
              isFetchingParties: isFetchingInternalParties,
              placeholderContent: internalPartiesError
                ? {
                    message: partyLoadingErrorMessage,
                  }
                : internalParties.count === 0
                  ? {
                      message: noPartyFoundMessage,
                    }
                  : undefined,
              title: intl.formatMessage({
                id: 'ManagePartyLists.internalPartyListTitle',
                defaultMessage: 'Internal Party List',
                description: 'Internal party list section title',
              }),
              setPage: setInternalPage,
              state: {
                pageIndex: internalPage,
              },
            }
          : {
              data: externalParties,
              isFetchingParties: isFetchingExternalParties,
              placeholderContent: externalPartiesError
                ? {
                    message: partyLoadingErrorMessage,
                  }
                : externalParties.count === 0
                  ? {
                      message: noPartyFoundMessage,
                    }
                  : undefined,
              title: intl.formatMessage({
                id: 'ManagePartyLists.externalPartyListTitle',
                defaultMessage: 'External Party List',
                description: 'External party list section title',
              }),
              setPage: setExternalPage,
              state: {
                pageIndex: externalPage,
              },
            };

        return (
          <Section key={partyConfigs.title} title={partyConfigs.title}>
            <ContentContainer
              placeholderContent={partyConfigs.placeholderContent}
            >
              <Table
                data={partyConfigs.data.results}
                columns={columns}
                isLoading={partyConfigs.isFetchingParties}
                name={partyConfigs.title}
                options={TABLE_OPTIONS}
                getBulkActions={getBulkActions}
                rowActions={rowActions}
                totalCount={partyConfigs.data.count}
                state={partyConfigs.state}
                onPaginate={({ pageIndex }: PaginationParams) => {
                  partyConfigs.setPage(pageIndex);
                }}
              />
            </ContentContainer>
          </Section>
        );
      })}

      {modalProps?.action === 'edit' ? (
        <UpdatePartyModal {...(modalProps as ExtendedPartyModalProps)} />
      ) : null}
      {modalProps?.action === 'delete' ? (
        <DeletePartyModal {...(modalProps as ExtendedPartyModalProps)} />
      ) : null}
      {modalProps?.action === 'add' ? (
        <AddPartyModal {...(modalProps as BasePartyModalProps)} />
      ) : null}
      {modalProps?.action === 'upload' ? (
        <UploadPartyModal {...(modalProps as BasePartyModalProps)} />
      ) : null}
    </Layout>
  );

  return (
    <PageLayout
      actions={[
        {
          onClick: () => {
            setModalProps({
              action: 'add',
              clientId,
              onHide: handleHideModal,
            });
          },
          text: intl.formatMessage({
            id: 'ManagePartyLists.addNewPartyAction',
            defaultMessage: 'Add New Party',
            description: 'Add new party action button text',
          }),
        },
        {
          level: 'primary',
          onClick: () => {
            setModalProps({
              action: 'upload',
              clientId,
              onHide: handleHideModal,
            });
          },
          text: intl.formatMessage({
            id: 'ManagePartyLists.uploadAction',
            defaultMessage: 'Upload',
            description: 'Upload action button text',
          }),
        },
      ]}
      loadingContent={{
        isLoading: isFetchingExternalParties || isFetchingInternalParties,
        message: intl.formatMessage({
          id: 'ManagePartyLists.loadingMessage',
          defaultMessage: 'Loading party lists...',
          description: 'Loading message while fetching party lists',
        }),
      }}
      children={children}
      nav={nav}
      title={intl.formatMessage({
        id: 'ManagePartyLists.pageTitle',
        defaultMessage: 'Manage Party Lists',
        description: 'Manage party lists page title',
      })}
    />
  );
};

export default Page;
